import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { FaEye, FaTrash } from 'react-icons/fa';
import './AdminPages.css';

const ManageContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get('/api/contacts');
      setContacts(response.data.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (contact) => {
    setSelectedContact(contact);
    
    // Mark as read if it's new
    if (contact.status === 'new') {
      try {
        await axios.put(`/api/contacts/${contact._id}`, { status: 'read' });
        fetchContacts();
      } catch (error) {
        console.error('Error updating contact status:', error);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await axios.delete(`/api/contacts/${id}`);
        fetchContacts();
        if (selectedContact?._id === id) {
          setSelectedContact(null);
        }
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`/api/contacts/${id}`, { status });
      fetchContacts();
      if (selectedContact?._id === id) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Contact Messages">
        <div className="loading-state">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Contact Messages">
      <div className="admin-page">
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Company</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact._id} className={contact.status === 'new' ? 'unread-row' : ''}>
                  <td>
                    <select
                      value={contact.status}
                      onChange={(e) => updateStatus(contact._id, e.target.value)}
                      className="status-select"
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                  <td className="font-semibold">{contact.name}</td>
                  <td>{contact.email}</td>
                  <td>{contact.subject}</td>
                  <td>{contact.company || '-'}</td>
                  <td>{formatDate(contact.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-view" onClick={() => handleView(contact)}>
                        <FaEye />
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(contact._id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {contacts.length === 0 && (
            <div className="no-data">No messages yet.</div>
          )}
        </div>
      </div>

      {/* Message Detail Modal */}
      {selectedContact && (
        <div className="modal-overlay" onClick={() => setSelectedContact(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Message Details</h2>
            
            <div className="contact-detail">
              <div className="detail-row">
                <strong>From:</strong>
                <span>{selectedContact.name}</span>
              </div>
              <div className="detail-row">
                <strong>Email:</strong>
                <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
              </div>
              {selectedContact.phone && (
                <div className="detail-row">
                  <strong>Phone:</strong>
                  <span>{selectedContact.phone}</span>
                </div>
              )}
              {selectedContact.company && (
                <div className="detail-row">
                  <strong>Company:</strong>
                  <span>{selectedContact.company}</span>
                </div>
              )}
              <div className="detail-row">
                <strong>Date:</strong>
                <span>{formatDate(selectedContact.createdAt)}</span>
              </div>
              <div className="detail-row">
                <strong>Subject:</strong>
                <span>{selectedContact.subject}</span>
              </div>
            </div>

            <div className="message-content">
              <strong>Message:</strong>
              <p>{selectedContact.message}</p>
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setSelectedContact(null)}
              >
                Close
              </button>
              <a
                href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                className="btn-save"
                style={{ textDecoration: 'none' }}
              >
                Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ManageContacts;
